import * as React from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Spinner } from './spinner';
import { useNavigate } from 'react-router-dom';

// =============================================================================
// Types
// =============================================================================

export type FilterIcon = React.ComponentType<{ className?: string }>;

/**
 * Action to perform when a suggestion is selected
 */
export type FilterAction =
  | { type: 'filter' } // Add as a filter bubble
  | { type: 'navigate'; url: string }; // Navigate to a URL

/**
 * A suggestion returned by a filter generator
 */
export interface FilterSuggestion {
  value: string;
  displayLabel: string;
  displayValue: string;
  /** Override the default action for this specific suggestion */
  action?: FilterAction;
}

/**
 * Context passed to filter generators
 */
export interface FilterGeneratorContext<T> {
  query: string;
  activeFilters: ActiveFilter<T>[];
}

/**
 * Serialized filter data for URL persistence.
 * Used with createFilterHash to generate URL hashes for linking to filtered views.
 */
export interface SerializedFilter<T> {
  /** The filter type (must match a FilterDefinition<T> type) */
  type: T;
  /** The filter value */
  value: string;
  /** The display value shown in the filter bubble */
  displayValue: string;
}

/**
 * Definition for a filter type
 */
export interface FilterDefinition<T> {
  /** Unique identifier for this filter type */
  readonly type: T;

  /** Icon component to display for this filter type */
  readonly icon: FilterIcon;

  /** Priority for ordering in results (lower = higher priority) */
  readonly priority: number;

  /**
   * Whether this filter type is unique (can only be selected once).
   * If true, generation will be skipped when a filter of this type is already active.
   */
  readonly isUnique: boolean;

  /**
   * Default action when a suggestion of this type is selected.
   * Can be overridden per-suggestion in FilterSuggestion.
   * Defaults to { type: 'filter' } if not specified.
   */
  readonly defaultAction?: FilterAction;

  /**
   * Generate suggestions for this filter type.
   * Can be async to support backend calls.
   * Return empty array if no matches.
   */
  generate: (
    context: FilterGeneratorContext<T>
  ) => Promise<FilterSuggestion[]> | FilterSuggestion[];

  /**
   * Deserialize a filter from URL hash data.
   * Takes the serialized filter object and returns an ActiveFilter<T>.
   * Required for URL hash persistence to work.
   */
  deserialize: (serialized: SerializedFilter<T>) => ActiveFilter<T> | null;
}

/**
 * An active filter applied to the search
 */
export interface ActiveFilter<T> {
  id: string;
  type: T;
  value: string;
  displayLabel: string;
  displayValue: string;
}

// =============================================================================
// Internal Types
// =============================================================================

interface SearchSuggestion<T> {
  type: T;
  icon: FilterIcon;
  value: string;
  displayLabel: string;
  displayValue: string;
  action: FilterAction;
}

// =============================================================================
// URL Hash Utilities
// =============================================================================

/**
 * Parse filters from a URL hash string.
 * The hash contains a JSON-encoded array of SerializedFilter<T> objects.
 * Uses each filter definition's deserialize function to reconstruct full ActiveFilter<T>s.
 *
 * @param hash - The hash string (with or without # prefix)
 * @param definitions - The filter definitions (must include deserialize for each filter type)
 * @returns Array of parsed filters
 */
export const parseFiltersFromHash = <T,>(
  hash: string,
  definitions: FilterDefinition<T>[]
): ActiveFilter<T>[] => {
  if (!hash || hash === '#') {
    return [];
  }

  // Remove leading # if present
  const hashContent = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!hashContent) {
    return [];
  }

  try {
    const decoded = decodeURIComponent(hashContent);
    const serializedFilters: SerializedFilter<T>[] = JSON.parse(decoded);

    if (!Array.isArray(serializedFilters)) {
      return [];
    }

    const filters: ActiveFilter<T>[] = [];
    for (const serialized of serializedFilters) {
      const definition = definitions.find((d) => d.type === serialized.type);
      if (definition?.deserialize) {
        const filter = definition.deserialize(serialized);
        if (filter) {
          filters.push(filter);
        }
      }
    }

    return filters;
  } catch {
    return [];
  }
};

/**
 * Create a URL hash string (with # prefix) for linking to a page with pre-populated filters.
 * Useful for creating links from other pages that navigate to a filtered view.
 *
 * @param filters - Array of serialized filters with type, value, and displayValue
 * @returns Hash string with # prefix containing JSON-encoded filters
 *
 * @example
 * // Create a link to workspaces filtered by state and preset
 * const hash = createFilterHash([
 *   { type: 'state', value: 'WORKING', displayValue: 'Working' },
 *   { type: 'preset', value: 'GENERATE_TEMPLATE', displayValue: 'Generate Template' }
 * ]);
 * // Returns: "#[{\"type\":\"state\",\"value\":\"WORKING\",...}]"
 * navigate(`/copilot/workspaces${hash}`);
 */
export const createFilterHash = <T,>(filters: SerializedFilter<T>[]): string => {
  if (filters.length === 0) {
    return '';
  }

  const json = JSON.stringify(filters);
  return `#${encodeURIComponent(json)}`;
};

/**
 * Hook to get initial filters from the current URL hash.
 * This is evaluated once on mount and returns a stable reference.
 *
 * @param definitions - The filter definitions to use for parsing
 * @returns Array of filters parsed from the URL hash
 *
 * @example
 * const initialFilters = useFiltersFromHash(MY_FILTER_DEFINITIONS);
 * const [activeFilters, setActiveFilter<T>s] = useState(initialFilters);
 */
export const useFiltersFromHash = <T,>(definitions: FilterDefinition<T>[]): ActiveFilter<T>[] =>
  React.useMemo(() => parseFiltersFromHash(window.location.hash, definitions), []); // Only run once on mount

/**
 * Hook to sync filter state with the URL hash.
 * Updates the URL hash when filters change (without adding to browser history).
 * Serializes filters as a JSON array of SerializedFilter<T> objects.
 *
 * @param filters - The current active filters
 *
 * @example
 * const [activeFilters, setActiveFilter<T>s] = useState(initialFilters);
 * useFilterHashSync(activeFilters);
 */
export const useFilterHashSync = <T,>(filters: ActiveFilter<T>[]) =>
  React.useEffect(() => {
    if (filters.length === 0) {
      // Remove hash if no filters
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }

    const serialized: SerializedFilter<T>[] = filters.map((f) => ({
      type: f.type,
      value: f.value,
      displayValue: f.displayValue
    }));
    const json = JSON.stringify(serialized);
    window.history.replaceState(null, '', `#${encodeURIComponent(json)}`);
  }, [filters]);

// =============================================================================
// localStorage Persistence
// =============================================================================

interface StoredView<SortKey> {
  filters?: SerializedFilter<unknown>[];
  sortBy?: SortKey;
  sortDirection?: 'asc' | 'desc';
}

export interface UseFiltersFromLocalStorageOptions<SortKey> {
  defaultSortDirection?: 'asc' | 'desc';
  defaultSortBy?: SortKey;
  preferHash?: boolean;
}

export interface UseFiltersFromLocalStorageResult<T, SortKey> {
  activeFilters: ActiveFilter<T>[];
  setActiveFilters: (filters: ActiveFilter<T>[]) => void;
  initialFilters: ActiveFilter<T>[];
  sortBy: SortKey | undefined;
  sortDirection: 'asc' | 'desc';
  setSort: (key: SortKey, direction: 'asc' | 'desc') => void;
}

/**
 * Hook that manages filter and sort state with automatic localStorage persistence.
 *
 * @param storageKey - localStorage key to persist state under
 * @param filterDefinitions - filter definitions for deserialization
 * @param options - optional default sort configuration
 *
 * @example
 * const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
 *   useFiltersFromLocalStorage('my_page_view', filterDefinitions, {
 *     defaultSortDirection: 'desc',
 *   });
 */
export const useFiltersFromLocalStorage = <T, SortKey extends string = string>(
  storageKey: string,
  filterDefinitions: FilterDefinition<T>[],
  options?: UseFiltersFromLocalStorageOptions<SortKey>
): UseFiltersFromLocalStorageResult<T, SortKey> => {
  const stored = React.useMemo<StoredView<SortKey>>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    } catch {
      return {};
    }
  }, []);

  const initialFilters = React.useMemo<ActiveFilter<T>[]>(() => {
    if (options?.preferHash) {
      const hashFilters = parseFiltersFromHash(window.location.hash, filterDefinitions);
      if (hashFilters.length > 0) return hashFilters;
    }
    if (!stored.filters || !Array.isArray(stored.filters)) return [];
    const filters: ActiveFilter<T>[] = [];
    for (const serialized of stored.filters) {
      const definition = filterDefinitions.find((d) => d.type === serialized.type);
      if (definition?.deserialize) {
        const filter = definition.deserialize(serialized as SerializedFilter<T>);
        if (filter) filters.push(filter);
      }
    }
    return filters;
  }, []);

  const [activeFilters, setActiveFilters] = React.useState<ActiveFilter<T>[]>(initialFilters);

  const [sortBy, setSortBy] = React.useState<SortKey | undefined>(
    stored.sortBy ?? options?.defaultSortBy
  );
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(
    stored.sortDirection ?? options?.defaultSortDirection ?? 'desc'
  );

  const setSort = React.useCallback((key: SortKey, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDirection(direction);
  }, []);

  React.useEffect(() => {
    const serializedFilters: SerializedFilter<T>[] = activeFilters.map((f) => ({
      type: f.type,
      value: f.value,
      displayValue: f.displayValue
    }));
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        filters: serializedFilters,
        sortBy,
        sortDirection
      })
    );
  }, [activeFilters, sortBy, sortDirection, storageKey]);

  return {
    activeFilters,
    setActiveFilters,
    initialFilters,
    sortBy,
    sortDirection,
    setSort
  };
};

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Run all filter generators and collect suggestions as they complete
 */
const runFilterGenerators = <T,>(
  definitions: FilterDefinition<T>[],
  context: FilterGeneratorContext<T>,
  onUpdate: (suggestions: SearchSuggestion<T>[], isLoading: boolean) => void,
  signal: AbortSignal
) => {
  const activeFilterTypes = new Set(context.activeFilters.map((f) => f.type));

  // Filter out definitions that are unique and already selected
  const applicableDefinitions = definitions.filter(
    (def) => !def.isUnique || !activeFilterTypes.has(def.type)
  );

  if (applicableDefinitions.length === 0) {
    onUpdate([], false);
    return;
  }

  const allResults: SearchSuggestion<T>[] = [];
  let pendingCount = applicableDefinitions.length;

  // Run all generators in parallel, updating as each completes
  applicableDefinitions.forEach(async (def) => {
    try {
      const partialResults = await def.generate(context);
      if (signal.aborted) return;

      // Convert to SearchSuggestion<T> with type, icon, and action
      const suggestions: SearchSuggestion<T>[] = partialResults.map((r) => ({
        type: def.type,
        icon: def.icon,
        value: r.value,
        displayLabel: r.displayLabel,
        displayValue: r.displayValue,
        action: r.action ?? def.defaultAction ?? { type: 'filter' }
      }));

      // Add results and sort by priority
      allResults.push(...suggestions);
      allResults.sort((a, b) => {
        const priorityA = definitions.find((d) => d.type === a.type)?.priority ?? 999;
        const priorityB = definitions.find((d) => d.type === b.type)?.priority ?? 999;
        return priorityA - priorityB;
      });

      pendingCount--;
      onUpdate([...allResults], pendingCount > 0);
    } catch {
      // Filter generation failed - continue with other filters
      pendingCount--;
      if (!signal.aborted) {
        onUpdate([...allResults], pendingCount > 0);
      }
    }
  });
};

// =============================================================================
// Component
// =============================================================================

export interface SearchFilterInputProps<T> {
  /** Filter definitions that determine what suggestions are generated */
  filterDefinitions: FilterDefinition<T>[];
  /** Called when the active filters change */
  onFiltersChange: (filters: ActiveFilter<T>[]) => void;
  /** Placeholder text when no filters are active */
  placeholder?: string;
  /** Initial filters to populate the search input with (uncontrolled) */
  initialFilters?: ActiveFilter<T>[];
  /** Accessible name for the text input; the placeholder disappears once filters are active. */
  ariaLabel?: string;
  /** `compact` fits narrow surfaces such as the Smart Fill sidebar. */
  size?: 'default' | 'compact';
}

export const SearchFilterInput = <T,>({
  filterDefinitions,
  onFiltersChange,
  placeholder = 'Search...',
  initialFilters,
  ariaLabel,
  size = 'default'
}: SearchFilterInputProps<T>) => {
  const compact = size === 'compact';
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Cancel the deferred close on unmount; the callback sets state.
  const closeSuggestionsTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  React.useEffect(() => () => clearTimeout(closeSuggestionsTimer.current), []);

  // Internal state (uncontrolled component)
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilter<T>[]>(initialFilters ?? []);
  const [searchValue, setSearchValue] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<SearchSuggestion<T>[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Update filters helper
  const updateFilters = React.useCallback(
    (newFilters: ActiveFilter<T>[]) => {
      setActiveFilters(newFilters);
      onFiltersChange(newFilters);
    },
    [onFiltersChange]
  );

  // Reset selected index when suggestions change
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions]);

  // Generate suggestions when search value changes
  React.useEffect(() => {
    const query = searchValue.trim();

    if (!query) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    const abortController = new AbortController();
    setSuggestionsLoading(true);

    runFilterGenerators(
      filterDefinitions,
      { query, activeFilters },
      (newSuggestions, isLoading) => {
        setSuggestions(newSuggestions);
        setSuggestionsLoading(isLoading);
      },
      abortController.signal
    );

    return () => {
      abortController.abort();
    };
  }, [searchValue, activeFilters, filterDefinitions]);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: SearchSuggestion<T>) => {
    // Handle action based on type
    if (suggestion.action.type === 'navigate') {
      navigate(suggestion.action.url);
      return;
    }

    // Default: add as filter
    const newFilter: ActiveFilter<T> = {
      id: `${suggestion.type}-${suggestion.value}-${Date.now()}`,
      type: suggestion.type,
      value: suggestion.value,
      displayLabel: suggestion.displayLabel,
      displayValue: suggestion.displayValue
    };

    updateFilters([...activeFilters, newFilter]);
    setSearchValue('');
  };

  // Handle filter removal
  const handleRemoveFilter = (filterId: string) => {
    updateFilters(activeFilters.filter((f) => f.id !== filterId));
  };

  // Handle clearing all
  const handleClearAll = () => {
    setSearchValue('');
    updateFilters([]);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Backspace' && searchValue === '' && activeFilters.length > 0) {
        // Remove last filter when backspacing on empty input
        handleRemoveFilter(activeFilters[activeFilters.length - 1].id);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && suggestions[selectedIndex]) {
      e.preventDefault();
      handleSuggestionSelect(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      inputRef.current?.blur();
    } else if (e.key === 'Backspace' && searchValue === '' && activeFilters.length > 0) {
      handleRemoveFilter(activeFilters[activeFilters.length - 1].id);
    }
  };

  return (
    <div className="relative">
      <div
        className={`relative flex items-center rounded-lg border border-neutral-300 bg-white shadow-sm ${compact ? 'min-h-9 gap-2 px-2.5 py-1' : 'min-h-12 gap-3 px-3 py-2.5'} focus-within:border-sky-500 focus-within:ring-1 focus-within:ring-sky-500 dark:border-neutral-600 dark:bg-neutral-800 dark:focus-within:border-sky-400 dark:focus-within:ring-sky-400`}
      >
        <MagnifyingGlassIcon
          className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} flex-shrink-0 text-neutral-400 dark:text-neutral-500`}
        />

        {/* Wrappable area for filters and input */}
        <div
          className={`flex min-w-0 flex-1 flex-wrap items-center ${compact ? 'gap-1.5' : 'gap-2'}`}
        >
          {/* Active filter bubbles */}
          {activeFilters.map((filter) => (
            <span
              key={filter.id}
              className={`inline-flex items-center rounded-md bg-sky-600 text-white ${compact ? 'gap-1 px-2 py-0.5 text-xs' : 'gap-1.5 px-2.5 py-1 text-sm'}`}
            >
              <span className="text-sky-100">{filter.displayLabel}</span>
              <span className="font-medium">{filter.displayValue}</span>
              <button
                type="button"
                onClick={() => handleRemoveFilter(filter.id)}
                aria-label={`Remove ${filter.displayLabel} ${filter.displayValue}`}
                className="ml-0.5 rounded p-0.5 hover:bg-sky-700"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => {
              closeSuggestionsTimer.current = setTimeout(() => setShowSuggestions(false), 150);
            }}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            placeholder={activeFilters.length > 0 ? '' : placeholder}
            className={`${compact ? 'min-w-[80px] py-0.5 text-xs' : 'min-w-[150px] py-1 text-sm'} flex-1 border-0 bg-transparent px-0 text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-neutral-500`}
          />
        </div>

        {(searchValue || activeFilters.length > 0) && (
          <button
            type="button"
            onClick={handleClearAll}
            aria-label="Clear search"
            className="flex-shrink-0 rounded p-1 text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (suggestions.length > 0 || suggestionsLoading) && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-80 overflow-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
          {suggestions.map((suggestion, index) => {
            const isSelected = index === selectedIndex;
            const Icon = suggestion.icon;

            return (
              <button
                key={`${suggestion.type}-${suggestion.value}-${index}`}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionSelect(suggestion);
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`flex w-full items-center text-left ${compact ? 'gap-2 px-2.5 py-1.5 text-xs' : 'gap-3 px-3 py-2 text-sm'} ${isSelected ? 'bg-sky-50 dark:bg-sky-900/20' : ''}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0 text-neutral-400 dark:text-neutral-500" />
                <span className="flex-1 text-neutral-600 dark:text-neutral-300">
                  {suggestion.displayLabel}{' '}
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    {suggestion.displayValue}
                  </span>
                </span>
              </button>
            );
          })}
          {suggestionsLoading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">
              <Spinner size="size-4" color="neutral" />
              <span>Loading suggestions...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
